/**
 * Catalog filtering logic — two modes:
 *
 * **Icon-marking (Mode B)**: All option values are visible with tick/X marks
 * indicating compatibility. Used by most catalogs.
 *
 * **Cascading (Mode A)**: Fields are selected top-to-bottom. Each selection
 * constrains downstream fields. Only compatible values are shown (no tick/X).
 * Used by Cook catalogs (part.cascadeFilter = true).
 *
 * Pure functions — no React or service dependencies.
 */

import type {
    CompatibilityStatus,
    FieldCompatibilityMap,
    FieldSelections,
    PartCompatibilityMap,
} from './assembly-types';
import type { ICatalogItem, ICatalogPart } from './types';

/**
 * Get distinct values for a column, filtered by selections on other columns.
 * Used to determine which values are reachable given current selections.
 */
export function getDistinctValues(
    items: ICatalogItem[],
    column: string,
    selections: FieldSelections
): Set<string | number> {
    const filtered = items.filter((item) =>
        Object.entries(selections).every(([col, val]) => col === column || val == null || item[col] === val)
    );
    return new Set(filtered.map((item) => item[column]));
}

/**
 * Compute compatibility for a single field column.
 *
 * For each distinct value of `column` in the part's items, checks if
 * selecting that value would produce at least one valid item given
 * the current selections on all other columns.
 */
export function computeFieldCompatibility(
    part: ICatalogPart,
    selections: FieldSelections,
    column: string
): FieldCompatibilityMap {
    const result: FieldCompatibilityMap = new Map();
    const reachable = getDistinctValues(part.items, column, selections);

    // Collect all distinct values for this column across all items
    const allValues = new Set(part.items.map((item) => item[column]));

    for (const value of allValues) {
        const status: CompatibilityStatus = reachable.has(value) ? 'compatible' : 'incompatible';
        result.set(value, status);
    }

    return result;
}

/**
 * Compute compatibility maps for all fields of a part at once.
 */
export function computeAllFieldsCompatibility(
    part: ICatalogPart,
    selections: FieldSelections
): PartCompatibilityMap {
    const result: PartCompatibilityMap = new Map();

    for (const field of part.fields) {
        result.set(field.column, computeFieldCompatibility(part, selections, field.column));
    }

    return result;
}

// ============================================================================
// Cascading Filter (Mode A)
// ============================================================================

/**
 * Filter items using only the fields above `fieldIndex` (top-down constraint).
 * Fields at or below `fieldIndex` are unconstrained.
 */
function filterByUpperFields(
    items: ICatalogItem[],
    fields: { column: string }[],
    selections: FieldSelections,
    fieldIndex: number
): ICatalogItem[] {
    return items.filter((item) => {
        for (let i = 0; i < fieldIndex; i++) {
            const col = fields[i].column;
            const val = selections[col];
            if (val != null && item[col] !== val) return false;
        }
        return true;
    });
}

/**
 * Compute available options for one field in cascade mode.
 *
 * Only selections on fields **above** the target field constrain the result.
 * Fields below are ignored (not yet selected in the cascade flow).
 */
export function computeCascadingOptions(
    part: ICatalogPart,
    selections: FieldSelections,
    fieldIndex: number
): Set<string | number> {
    const filtered = filterByUpperFields(part.items, part.fields, selections, fieldIndex);
    const column = part.fields[fieldIndex].column;
    return new Set(filtered.map((item) => item[column]));
}

/**
 * Compute cascading options for all fields and auto-select single-option fields.
 *
 * Returns the effective selections (with auto-fills applied) and per-field option sets.
 * A field is disabled if any field above it has no selection.
 */
export function computeAllCascadingOptions(
    part: ICatalogPart,
    selections: FieldSelections
): { effectiveSelections: FieldSelections; optionsPerField: Map<string, Set<string | number>> } {
    const effective: FieldSelections = { ...selections };
    const optionsPerField = new Map<string, Set<string | number>>();

    for (let i = 0; i < part.fields.length; i++) {
        const col = part.fields[i].column;

        // Check if all fields above have a selection
        let allAboveSelected = true;
        for (let j = 0; j < i; j++) {
            if (effective[part.fields[j].column] == null) {
                allAboveSelected = false;
                break;
            }
        }

        if (!allAboveSelected) {
            // Field is disabled — no options available
            optionsPerField.set(col, new Set());
            effective[col] = null;
            continue;
        }

        const options = computeCascadingOptions(part, effective, i);
        optionsPerField.set(col, options);

        // Auto-select if exactly one option and no current selection
        if (options.size === 1 && effective[col] == null) {
            effective[col] = [...options][0];
        }

        // Clear selection if it's no longer in the available options
        if (effective[col] != null && !options.has(effective[col] as string | number)) {
            effective[col] = null;
        }
    }

    return { effectiveSelections: effective, optionsPerField };
}
